import React from "react";
import { BarChart3Icon, BellIcon, CreditCardIcon, FileTextIcon, HomeIcon, LayoutGridIcon, LeafIcon, MessageSquareIcon, MoreHorizontalIcon, RecycleIcon, SearchIcon, UserIcon, UsersIcon, WalletIcon, BoxIcon } from "lucide-react";
import { BROSSARD_IMAGES, dashCopy, phoneCopy, Bi } from "../../data/brossardCase";
type Pick = (b: Bi) => string;
const TILE_ICONS: Record<(typeof phoneCopy.tiles)[number]['key'], BoxIcon> = {
  collect: RecycleIcon,
  requests: FileTextIcon,
  permits: FileTextIcon,
  payments: WalletIcon,
  comms: MessageSquareIcon,
  more: MoreHorizontalIcon
};
const TAB_ICONS: BoxIcon[] = [HomeIcon, LayoutGridIcon, CreditCardIcon, MessageSquareIcon, UserIcon];
const NAV_ICONS: BoxIcon[] = [LayoutGridIcon, UsersIcon, FileTextIcon, RecycleIcon, FileTextIcon, BellIcon, WalletIcon, BarChart3Icon];
function BrossardMark({
  small
}: {
  small?: boolean;
}) {
  return <span className={`inline-flex items-center gap-1 font-sans font-bold text-[#121A3A] ${small ? 'text-[9px]' : 'text-[13px]'}`} data-id="element-1215" data-mp="343ncuw0ifetn">
      <LeafIcon className={`${small ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5'} text-[#1F9D55]`} strokeWidth={2.4} aria-hidden="true" data-id="element-1216" data-mp="x20dhounx0tr" />
      Brossard
    </span>;
}
function QrBlock() {
  // Deterministic pseudo-QR pattern (decorative)
  const cells = Array.from({
    length: 49
  }, (_, i) => (i * 7 + i % 5 * 3) % 5 < 2 ? 1 : 0);
  return <span className="grid grid-cols-7 gap-[1px] w-9 h-9 p-1 bg-white rounded-[4px] shrink-0" aria-hidden="true" data-id="element-1217" data-mp="s8founeylj78">
      {cells.map((c, i) => <span key={i} className={c ? 'bg-[#121A3A]' : 'bg-transparent'} data-id="element-1218" data-mp="j9apjevkplu0" data-mpi={i} />)}
    </span>;
}
export function BrossardPhone({
  pick
}: {
  pick: Pick;
}) {
  return <div className="w-[200px] rounded-[30px] bg-[#1B1F2E] p-[6px] shadow-[0_30px_60px_-24px_rgba(18,26,58,0.45)]" data-id="element-1219" data-mp="4durkyrvh5sc">
      <div className="relative rounded-[25px] bg-white overflow-hidden" data-id="element-1220" data-mp="9dh4f73sgk0m">
        <div className="flex items-center justify-between px-4 pt-2.5 font-sans text-[8px] font-semibold text-[#121A3A]" data-id="element-1221" data-mp="1yrm6acp26fun">
          <span data-id="element-1222" data-mp="23nbbownreqyv">9:41</span>
          <span className="w-14 h-3.5 rounded-full bg-[#1B1F2E]" aria-hidden="true" data-id="element-1223" data-mp="227yv49ogs8d4" />
          <span className="tracking-tighter" data-id="element-1224" data-mp="225vxp4kzfcyd">●●● ▮</span>
        </div>
        <div className="px-3 pt-3 flex items-center justify-between" data-id="element-1225" data-mp="1x7ko8uyln4go">
          <BrossardMark data-id="element-1226" data-mp="1sb3xru3xge0f" />
          <SearchIcon className="w-3 h-3 text-[#4A5268]" aria-hidden="true" data-id="element-1227" data-mp="1sakof1scexy0" />
        </div>
        <div className="mx-3 mt-2.5 rounded-xl bg-[#4F46E5] overflow-hidden text-white" data-id="element-1228" data-mp="1o9sdm43tokzm">
          <div className="flex items-start justify-between p-2.5 pb-1.5" data-id="element-1229" data-mp="2kxewt0bvy6v4">
            <div data-id="element-1230" data-mp="2fz536u5t7ebm">
              <p className="font-sans text-[9px] font-semibold" data-id="element-1231" data-mp="2g0oioijn5lcc">{pick(phoneCopy.card)}</p>
              <p className="mt-1.5 font-sans text-[8px] font-medium" data-id="element-1232" data-mp="2b854bjd4qm6h">{phoneCopy.name}</p>
              <p className="font-sans text-[7px] text-white/75" data-id="element-1233" data-mp="277v12egq29vb">{pick(phoneCopy.role)}</p>
            </div>
            <QrBlock data-id="element-1234" data-mp="33phgkkjtq39l" />
          </div>
          <img src={BROSSARD_IMAGES.cardPark} alt="" className="w-full h-10 object-cover" data-id="element-1235" data-mp="33qiv4z01t7w2" />
        </div>
        <p className="px-3 mt-3 font-sans text-[8.5px] font-semibold text-[#121A3A]" data-id="element-1236" data-mp="2ywelg7zeelra">{pick(phoneCopy.services)}</p>
        <div className="px-3 mt-1.5 grid grid-cols-3 gap-1.5" data-id="element-1237" data-mp="li7tj7z2qtl3">
          {phoneCopy.tiles.map(t => {
          const Icon = TILE_ICONS[t.key];
          return <div key={t.key} className="rounded-lg border border-[#EEEDF6] py-1.5 flex flex-col items-center gap-1" data-id="element-1238" data-mp="ufi74zzd6ut6">
                <span className="w-5 h-5 rounded-md bg-[#F1F0FD] flex items-center justify-center" data-id="element-1239" data-mp="zh7dw3fk0mbg">
                  <Icon className="w-2.5 h-2.5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-1240" data-mp="14eh2jgv4cty8" />
                </span>
                <span className="font-sans text-[6.5px] text-[#3A4260] whitespace-nowrap" data-id="element-1241" data-mp="19bhaxyffy8si">{pick(t.label)}</span>
              </div>;
        })}
        </div>
        <div className="mt-3 border-t border-[#EEEDF6] px-2 py-1.5 flex justify-between" data-id="element-1242" data-mp="1i52r9m797y41">
          {phoneCopy.tabs.map((tab, i) => {
          const Icon = TAB_ICONS[i];
          return <span key={tab.fr} className={`flex flex-col items-center gap-0.5 ${i === 0 ? 'text-[#4F46E5]' : 'text-[#8A8FA6]'}`} data-id="element-1243" data-mp="1s3s4v2s4aweh" data-mpi={i}>
                <Icon className="w-2.5 h-2.5" strokeWidth={2} aria-hidden="true" data-id="element-1244" data-mp="1eg07fib474cr" />
                <span className="font-sans text-[5.5px]" data-id="element-1245" data-mp="19hgn1mc660mi">{pick(tab)}</span>
              </span>;
        })}
        </div>
      </div>
    </div>;
}
export function BrossardLaptop({
  pick,
  lang
}: {
  pick: Pick;
  lang: 'FR' | 'EN';
}) {
  const months = lang === 'FR' ? dashCopy.months : dashCopy.monthsEn;
  // data-note: {dashCopy.demoNote}
  return <div className="w-[400px]" data-id="element-1246" data-mp="vkjwllzyql65">
      <div className="rounded-t-[14px] bg-[#1B1F2E] p-[6px] pb-[8px]" data-id="element-1247" data-mp="qk5l1tz5kt3d">
        <div className="rounded-[8px] bg-white overflow-hidden flex h-[228px]" data-id="element-1248" data-mp="qm05l76iij6x">
          <aside className="w-[88px] shrink-0 border-r border-[#EEEDF6] bg-[#FBFBFE] px-2 py-2.5" data-id="element-1249" data-mp="15eto71h2zfft">
            <BrossardMark small data-id="element-1250" data-mp="10jwia3f3lm75" />
            <ul className="mt-3 space-y-1" data-id="element-1251" data-mp="2gvtfpuvltq8s">
              {dashCopy.nav.map((n, i) => {
              const Icon = NAV_ICONS[i];
              return <li key={n.fr} className={`flex items-center gap-1 rounded px-1 py-[3px] font-sans text-[6px] ${i === 0 ? 'bg-[#EDEBFD] text-[#4F46E5] font-semibold' : 'text-[#5A6178]'}`} data-id="element-1252" data-mp="2bw739nnloe11" data-mpi={i}>
                    <Icon className="w-2 h-2 shrink-0" strokeWidth={2} aria-hidden="true" data-id="element-1253" data-mp="2ogm3182annwg" />
                    <span className="truncate" data-id="element-1254" data-mp="2aguwtti0rbt0">{pick(n)}</span>
                  </li>;
            })}
            </ul>
          </aside>
          <div className="flex-1 min-w-0 p-2.5" data-id="element-1255" data-mp="2eholip3acumy">
            <div className="flex items-center justify-between" data-id="element-1256" data-mp="326h99y9r7w6h">
              <p className="font-sans text-[9px] font-semibold text-[#121A3A]" data-id="element-1257" data-mp="3292aqm654a3p">{pick(dashCopy.title)}</p>
              <span className="w-16 h-3 rounded bg-[#F3F3F8]" aria-hidden="true" data-id="element-1258" data-mp="377wsrlwa9umr" />
            </div>
            <div className="mt-2 grid grid-cols-3 gap-1.5" data-id="element-1259" data-mp="2tcupl6itmjdn">
              {dashCopy.kpis.map(k => <div key={k.label.fr} className="rounded-md border border-[#EEEDF6] p-1.5" data-id="element-1260" data-mp="2xe69cvf0kadx">
                  <p className="font-sans text-[10px] font-semibold text-[#121A3A] leading-none" data-id="element-1261" data-mp="3l4k44tozgnc7">{pick(k.value)}</p>
                  <p className="mt-1 font-sans text-[5.5px] text-[#7A8098] truncate" data-id="element-1262" data-mp="3pyel3pd7eo7l">{pick(k.label)}</p>
                </div>)}
            </div>
            <div className="mt-2 grid grid-cols-[1.3fr_1fr] gap-1.5" data-id="element-1263" data-mp="3vqp5bomxdquv">
              <div className="rounded-md border border-[#EEEDF6] p-1.5" data-id="element-1264" data-mp="3qrvslyfgq3vr">
                <p className="font-sans text-[6.5px] font-semibold text-[#121A3A]" data-id="element-1265" data-mp="3qtyq13iy2zai">{pick(dashCopy.activity)}</p>
                <svg viewBox="0 0 120 56" className="mt-1 w-full h-[70px]" aria-hidden="true" data-id="element-1266" data-mp="3lv588ywmkm4j">
                  {[10, 22, 34, 46].map(y => <line key={y} x1="0" x2="120" y1={y} y2={y} stroke="#F0F0F6" strokeWidth="0.6" data-id="element-1267" data-mp="3c2g12g83xuf0" />)}
                  <polyline points="2,46 18,40 32,42 48,34 62,36 78,26 92,28 106,14 118,6" fill="none" stroke="#4F46E5" strokeWidth="1.6" strokeLinejoin="round" data-id="element-1268" data-mp="380us35yp20a3" />
                </svg>
                <div className="flex justify-between font-sans text-[5px] text-[#9AA0B4]" data-id="element-1269" data-mp="2y36um89peeo8">
                  {months.map(m => <span key={m} data-id="element-1270" data-mp="2t4dk8oppr3sa">{m}</span>)}
                </div>
              </div>
              <div className="rounded-md border border-[#EEEDF6] p-1.5" data-id="element-1271" data-mp="2j06vxncxg1wa">
                <p className="font-sans text-[6.5px] font-semibold text-[#121A3A]" data-id="element-1272" data-mp="2izx5jynyzsq2">{pick(dashCopy.top)}</p>
                <ul className="mt-1.5 space-y-1.5" data-id="element-1273" data-mp="2o1cnhhuupj48">
                  {dashCopy.topRows.map(r => <li key={r.label.fr} className="flex items-center justify-between font-sans text-[6px] text-[#3A4260]" data-id="element-1274" data-mp="2syw1qhrjtvw6">
                      <span className="flex items-center gap-1" data-id="element-1275" data-mp="2xv3bkln2frje">
                        <span className="w-1.5 h-1.5 rounded-sm bg-[#C7C3F8]" aria-hidden="true" data-id="element-1276" data-mp="20b0b9uskp07u" />
                        {pick(r.label)}
                      </span>
                      <span className="tabular-nums" data-id="element-1277" data-mp="2a7bmehgffk0p">{pick(r.value)}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mx-[-18px] h-[10px] rounded-b-[10px] bg-[#D9DBE3]" aria-hidden="true" data-id="element-1278" data-mp="1hjpjlbf0wjew" />
    </div>;
}